<template>
  <section
    :class="['hero', $route.name === 'home' ? 'hero--fullscreen' : false]"
    :style="{minHeight: isMobile ? `${windowH}px`: false, maxHeight: isMobile ? `${windowH + 1}px`: false}"
    @mousemove="mouseMove"
  >
    <div :class="['ui-container', 'ui-container--small']">
      <div class="hero__item" js-parallax="0.15">
        <h3 v-html="data.eyelet" js-anim="herotext"></h3>
        <h1 v-html="data.title" js-anim="herotitle"></h1>
        <h2 v-html="data.subline" js-anim="herotext"></h2>
      </div>
    </div>

    <div
      :class="['ui-container', 'ui-container--small', 'ui-container--bodymovin']"
    >
      <div class="hero__image-cont" js-anim="heropicture" ref="animCont">
        <div ref="animInit" v-show="!isReady"></div>
        <div ref="animLoop" v-show="isReady"></div>
        <div ref="animMouse"></div>
      </div>
    </div>

    <div class="scroll-down" js-scroll="">
      <div class="ui-container ui-container--small" js-anim="heropicture">
        <div class="c-button">
          <a href="#!" @click.prevent="scrollDown" aria-label="Scroll down">
            <div class="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14.1 8.6">
                <path d="M13.5.5l-6.4 7L.5.5" fill="none" stroke="#fff" stroke-width="1.5"/>
              </svg>
            </div>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import anime from 'animejs';
import Splitting from 'splitting';
import { mapGetters } from 'vuex';
import loop from '@/scripts/loop';

import initialState from '@/scripts/initialState';
import animations from '@/scripts/animations';
import { isMobileDevice, qsa, roundDec, lottieAnimation, lerp } from '@/scripts/utils';


export default {
  name: 'Hero',

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      baseUrl: process.env.BASE_URL,
      windowH: 0,
      windowW: window.innerWidth,

      svgAnimation: {},
      maxDimension: 0,
      maxDimensionTouch: 0,
      interMouse: 0,

      elsToAnimate: [],

      isReady: false,
      prevRoute: '',
      lerpedTime: {
        tValue: 0,
      },
      prevLerp: false,
    };
  },

  computed: {
    isMobile() {
      return isMobileDevice();
    },

    ...mapGetters({
      currentCity: 'getCity',
      currentRoute: 'getRouteName',
    }),

    ...mapGetters(['getStateLoader']),
  },

  watch: {
    getStateLoader(newVal) {
      if (!newVal && this.prevRoute !== this.$route.name) {
        this.prevRoute = this.$route.name;
        this.fetchDataAnimation();
      }
    },
  },

  created() {

  },

  mounted() {
    // this.fetchDataAnimation();

    this.windowH = window.innerHeight - 60;
    this.$eventHub.$on('windowResize', this.resize);

    loop.add(this.checkMouse);
  },

  methods: {
    resize() {
      if (window.innerWidth !== this.windowW) {
        this.windowH = window.innerHeight - 60;
        this.windowW = window.innerWidth;
      }

      if (!this.isMobile) {
        this.mouseMoveInit();
      } else {
        this.touchMoveInit();
      }
    },

    fetchDataAnimation() {
      fetch(`${this.baseUrl}static/data/${this.currentCity}/hero/${this.currentRoute}-data.json`)
        .then(response => response.json().then((data) => {
          if (data.init !== undefined) this.svgAnimation.init = lottieAnimation(this.$refs.animInit, data.init);
          this.svgAnimation.loop = lottieAnimation(this.$refs.animLoop, data.loop, true);
          this.svgAnimation.mouse = lottieAnimation(this.$refs.animMouse, data.mouse, true, true, false);

          this.svgAnimation.duration = this.svgAnimation.mouse.getDuration(true);

          if (data.init !== undefined) {
            this.svgAnimation.init.onComplete = () => {
              this.svgAnimation.init.destroy();
              this.isReady = true;
            };
          }

          if (this.svgAnimation.init === undefined) {
            this.isReady = true;
          }

          if (!this.isMobile) {
            this.mouseMoveInit();
          } else {
            this.touchMoveInit();
            window.addEventListener('deviceorientation', this.touchMove);
          }
        }));
    },

    mouseMoveInit() {
      this.maxDimension = this.$el.getBoundingClientRect().width;
      this.svgAnimation.multiplier = this.svgAnimation.duration / this.maxDimension;
    },

    touchMoveInit() {
      this.maxDimensionTouch = 90;
      this.svgAnimation.multiplierTouch = this.svgAnimation.duration / this.maxDimensionTouch;
    },

    mouseMove(event) {
      // this.interMouse = lerp(event.clientX, this.interMouse, 0.5);
      this.interMouse = event.clientX;
    },

    checkMouse() {
      if (this.svgAnimation.mouse !== undefined && !this.isMobile) {
        const time = roundDec(this.svgAnimation.multiplier * this.interMouse);
        this.lerpedTime.tValue = lerp(this.lerpedTime.tValue, Math.abs(time), 0.06);
        this.svgAnimation.mouse.goToAndStop(this.lerpedTime.tValue, true);
      }
    },

    touchMove(event) {
      if (this.svgAnimation.mouse !== undefined) {
        let delta = 0;

        if (window.innerWidth > window.innerHeight) {
          delta = (Math.max(Math.min(event.beta * -1, 45), -45) - 44);
        } else {
          delta = (Math.max(Math.min(event.gamma * -1, 45), -45) - 44);
        }

        const time = roundDec(this.svgAnimation.multiplierTouch * delta);
        this.svgAnimation.mouse.goToAndStop(Math.abs(time), true);
      }
    },

    scrollDown() {
      this.$eventHub.$emit('scrollTo', window.innerHeight + 100);
    },
  },

  beforeDestroy() {
    anime({
      targets: this.$refs.animCont,
      duration: 400,
      opacity: 0,
      easing: 'easeInOutSine',
      onComplete: () => {
        if (this.svgAnimation.init !== undefined) this.svgAnimation.init.destroy();
        this.svgAnimation.loop.destroy();
        this.svgAnimation.mouse.destroy();
      },
    });

    if (this.isMobile) window.removeEventListener('deviceorientation', this.touchMove);

    this.$eventHub.$off('windowResize', this.resize);
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/utils";

.hero {
  position: relative;
  width: 100%;
  padding: 40px 20px 0;
  margin-bottom: 0px;
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);

  @include mq(mobile) {
    padding: 40px 30px 0;
  }

  @include mq($from: mobileL, $until: ipadP) {
    padding-top: 0;
  }

  @include mq(ipadP) {
    padding: 120px 40px;
  }

  @include mq(ipadL) {
    padding: 120px 80px;
  }

  @include mq(ds) {
    padding: 70px 80px 120px;
  }

  &--fullscreen {
    min-height: 100vh;
    min-height: calc(var(--vh, 1vh) * 100);
    padding: 40px 20px 0;

    @include mq(mobile) {
      padding: 40px 30px;
    }

    @include mq($from: mobileL, $until: ipadP) {
      padding-top: 0px;
    }

    @include mq(ipadP) {
      padding: 120px 40px;
    }

    @include mq(ipadL) {
      padding: 120px 80px;
    }

    @include mq(ds) {
      padding: 70px 80px 120px;
    }
  }

  display: flex;
  align-items: stretch;
  justify-content: flex-start;

  @include mq(ipadL) {
    align-items: flex-start;
  }

  @include mq(ds) {
    align-items: center;
  }

  .ui-container {
    position: relative;
    height: auto;
  }

  .ui-container--bodymovin {
    position: absolute !important;
    top: 100px;
    height: calc(100% - 100px);

    @include mq($from: mobileL, $until: ipadP) {
      height: 100%;
    }
  }

  &__item {
    position: relative;
    display: inline-block;
    min-width: 50%;

    z-index: 2;
  }

  h1 {
    line-height: 1.2;
    margin-bottom: 20px;

    @include mq($until: ipadP) {
      font-size: 2.8rem;
    }

    @include mq($until: mobile) {
      font-size: 2.25rem;
    }

    @include mq($until: ds, $from: ipadP) {
      font-size: font-size(xssl);
    }
  }

  h2 {
  }

  h3 {
    letter-spacing: 2px;
    padding-left: 5px;
    text-transform: uppercase;
    margin-bottom: 20px;
    padding-left: 5px;

    @include mq(ipadP) {
      margin-bottom: 20px;
    }
  }

  h2,
  h3 {
    font-size: 16px;
    font-weight: normal;
  }

  &__image-cont {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    z-index: 1;

    z-index: 0;

    div {
      // @include aspect-ratio(14, 9);

      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
}

.scroll-down {
  position: absolute;
  top: calc(100% - 125px);
  left: 0;
  width: 100%;

  padding: 0 20px;

  transition: all 0.8s $global-ease 0.1s;

  &.fade-out {
    opacity: 0;
  }

  @include mq($from: mobileL, $until: ipadP) {
    display: none;
  }

  @include mq(mobile) {
    padding: 0px 30px;
  }

  @include mq(ds) {
    top: calc(100% - 200px);
    padding: 0px 40px;
  }

  @include mq(ipadL) {
    padding: 0px 80px;
  }

  .c-button {
    display: inline-block;
    position: relative;
    padding: 0;

    background-color: transparent;
    border: none;
    outline: transparent;

    transform: translate3d(0, 100px, 0);

    & a {
      padding: 10px;
      font-weight: 700;
      color: $color-light;

      z-index: 5;

      transition: transform 0.4s $global-ease;

      &:hover {
        .button__icon svg {
          animation: none;
          transform: translate3d(0, 0px, 0);
        }
      }
    }

    & a,
    &::before,
    &::after {
      display: inline-block;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    &::before,
    &::after {
      content: '';

      position: absolute;

      border: 1px solid $color-text;
      border-radius: 18px;

      transition: transform 0.4s $global-ease;

      @at-root [data-conference="new-york"] & {
        background-color: $color-azure;
      }
    }

    &::before {
      transform: translateX(3px) translateY(3px);

      @at-root [data-conference="new-york"] & {
        background-color: darken($color-azure, 15%);
      }
    }

    @at-root .pointerevents:not(.touchevents) &:hover::after,
    .no-pointerevents.no-touchevents &:hover::after {
      transform: translateX(3px) translateY(3px);
    }

    @at-root .pointerevents:not(.touchevents) &:hover a,
    .no-pointerevents.no-touchevents &:hover a {
      transform: translateX(3px) translateY(3px);
    }
  }

  .button__icon {
    width: 15px;
    overflow: hidden;

    svg {
      position: relative;
      width: 100%;

      animation: scroll 2.5s $global-ease infinite;
    }
  }
}

@keyframes scroll {
  0% {
    transform: translate3d(0, -16px, 0);
    opacity: 0;
  }

  20% {
    transform: translate3d(0, 0%, 0);
    opacity: 1;
  }

  80% {
    transform: translate3d(0, 0%, 0);
    opacity: 1;
  }

  100% {
    transform: translate3d(0, 16px, 0);
    opacity: 0;
  }
}
</style>
<style lang="scss">
.hero__image-cont svg {
  position: absolute;
  bottom: 0;
  // top: 150px;
  // left: 50%;
  width: auto !important;
  height: 80% !important;
  max-height: 500px;
  right: 0;

  @include mq(mobile) {
    right: 30px;
  }

  @include mq(ipadP) {
    right: 40px;
  }

  @include mq(ipadL) {
    right: 80px;
  }

  @include mq(containerSm) {
    right: 0;
  }

  // transform: translate3d(-50%, 0, 0) !important;

  @include mq(mobileL) {
    height: 100% !important;
    left: auto;
    max-height: 600px;
    top: auto;
    bottom: 20px;
    transform: translate3d(0%, 0%, 0) !important;
  }


  @include mq(ipadL) {
    height: 90% !important;
    max-height: 800px;
  }

  @include mq(ds) {
    top: 100px;
    right: 0;
    left: auto;
    max-height: 800px;
    top: 50%;
    transform: translate3d(0, -50%, 0) !important;
  }

  z-index: 0;

  @include mq($until: mobile) {
    max-width: 50%;
  }
}

.hero--fullscreen .hero__image-cont svg {
  bottom: 0px;
  left: 50%;

  height: 62% !important;
  max-height: 550px;
  transform: translate3d(-50%, 0%, 0) !important;

  @include mq(mobileL) {
    height: 100% !important;
    left: auto;
    max-height: 600px;
    top: auto;
    bottom: 20px;
    transform: translate3d(0%, 0%, 0) !important;
  }

  @include mq($from: ipadP, $until: ipadL) {
    height: 65% !important;
    max-height: 600px;
    bottom: 90px;
    top: auto;
    left: 50%;
    transform: translate3d(-50%, 0, 0) !important;
  }

  @include mq(ipadL) {
    bottom: auto;
    top: 50%;
    left: auto;
    height: 90% !important;
    max-height: 600px;

    transform: translate3d(0%, -50%, 0) !important;
  }

  @include mq(ds) {
    height: 80% !important;
    max-height: 600px;
  }
}

br.hidden-mobile {
  @include mq($until: ipadL, $and: $is-portrait) {
    display: none !important;
  }
}

[data-conference="new-york"] {
  .hero__image-cont.fix {
    @include mq(ipadL) {
      left: 7%;
    }
  }
}
</style>
